import { useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  adminApi,
  type AccessRow,
  type AccessSource,
  type AdminUser,
  type Application,
  type HeldAppRole,
  type UserAccess as Access,
} from '../lib/api.js';
import { useAsync, useAction, type Async } from '../lib/useAsync.js';
import { isImpliedRole, roleLabel } from '../lib/roles.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  HoldButton,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
} from '../components/ui.js';
import { InfoBubble } from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import { PickList } from '../components/PickList.js';
import { AppRolePicker } from '../modals/AppRoles.js';
/** The guard that made org-admin#28's multi-role save work, kept rather than
 *  reimplemented: it refuses a name the server does not list, by name, because
 *  the server's own refusal for that case arrives with an empty body. */
import { planRoleSave } from './UserDetail.js';

/**
 * One account's access, and where each piece of it came from.
 *
 * ── PROVENANCE IS THE POINT ──────────────────────────────────────────
 *
 * The question an administrator has on this screen is not "can they open the
 * Admin Portal" — the account either works or it does not — it is "why can they,
 * and what do I change to stop it". Before this, the portal showed a role
 * checkbox row and nothing else: a role arriving through a group looked
 * identical to one somebody had been given, and unticking it appeared to be the
 * fix when it was not.
 *
 * So every row names its sources, PLURAL. Somebody can hold the same role
 * directly and through a group at once, and a single "granted via" cell would be
 * right most of the time and wrong exactly when it matters: an admin reads "via
 * Admins", takes the person out of Admins, and the role stays.
 *
 * A greyed Remove means every source is inherited, so there is nothing on this
 * account to delete. One line at the bottom explains the greying once, rather
 * than a phrase repeated on every row.
 *
 * ── THE ROLE PICKER USED TO PIN INHERITED ROLES, AND DOES NOT NOW ────
 *
 * The old control seeded itself from `user.role`, which is the EFFECTIVE column
 * — direct ∪ group. Saving it sent that whole set to `/admin/set-role`, and
 * since RidgeWill/auth#269 that endpoint's result is mirrored into the table
 * that holds DIRECT assignments. So opening the modal and pressing Save, without
 * ticking anything, converted every group-inherited role into a direct one:
 * silently, with no visible change, and undoing the person's group membership
 * from then on withdrew nothing.
 *
 * The rows here are edited against the DIRECT set, which the server reports
 * separately for exactly this reason.
 */
export function UserAccessTab({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const { state, reload } = useAsync(() => adminApi.userAccess(user.id), [user.id]);

  return (
    <AsyncBoundary state={state}>
      {(access) => (
        /*
          FOUR PANELS, and the standing footnote is gone (Jon, 2026-08-28).

          These were bare `<section>`s with a heading, on a screen whose every
          other surface is a card — so the Access tab read as a different app
          from the Security groups screen next door. They are `Panel`s now, and
          each one's explanation moved into an `InfoBubble` on its header.

          The footnote that used to sit under all four ("a greyed Remove comes
          from a group") went with them. It was the same sentence every visit,
          restating something each row already says through its `Via` column and
          its own tooltip, and text present on every visit stops being read after
          the second one (org-admin#56).
        */
        <div style={{ display: 'grid', gap: '.9rem' }}>
          <Applications user={user} access={access} onChanged={() => { reload(); onChanged(); }} />
          <Roles user={user} access={access} onChanged={() => { reload(); onChanged(); }} />
          <AppRolesHeld user={user} access={access} onChanged={() => { reload(); onChanged(); }} />
          <Groups user={user} access={access} onChanged={() => { reload(); onChanged(); }} />
        </div>
      )}
    </AsyncBoundary>
  );
}

/**
 * An application's NAME, with its slug beneath it in monospace.
 *
 * The access read names applications by slug, because that is what the grant
 * write sends. A person calls it "Keyodo" — the chip rule: a chip carries the
 * identifier, a row label carries the name.
 *
 * The slug is shown either way, including while the listing is still in flight
 * and if it fails. It is what the record actually holds, and a row that renders
 * nothing until a second request lands looks like a grant that is not there.
 */
function AppName({ slug, known }: { slug: string; known: Async<{ applications: Application[] }> }) {
  const match =
    known.status === 'ready' ? known.data.applications.find((a) => a.slug === slug) : undefined;
  return (
    <>
      <div style={{ fontWeight: 600 }}>{match ? match.name : slug}</div>
      <code style={{ fontSize: '.78rem', color: C.muted }}>{slug}</code>
    </>
  );
}

/** Every source, as one readable cell. "Direct, Admins" rather than a guess at
 *  which one counts. */
function Via({ via }: { via: AccessSource[] }) {
  if (via.length === 0) return <span style={{ color: C.muted }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
      {via.map((v, i) => (
        <Badge key={i} tone={v.kind === 'direct' ? 'good' : 'neutral'}>
          {/* A chip, so a group is named by its SLUG. The other two are not
              identifiers at all — nothing has a slug for "everybody reaches
              this" — so they stay as words. */}
          {v.kind === 'direct'
            ? 'Direct'
            : v.kind === 'group'
              ? v.slug
              : v.reason === 'everybody'
                ? 'everyone'
                : 'break-glass role'}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Is there anything on THIS account to remove?
 *
 * Exported for its test, because getting it wrong fails silently in the worse
 * direction. An enabled Remove on a row whose only source is a group sends a
 * whole-set write of the DIRECT set — which does not contain that name, so
 * nothing changes, and the admin is told it was removed. A row that reports a
 * withdrawal that did not happen is how somebody comes to believe an account no
 * longer holds a role it still does.
 *
 * The implied sources are the same case and matter more: there is no row behind
 * `account` or the break-glass Admin Portal entitlement at all — the answer is
 * in a TypeScript file, and no button on this screen can reach it.
 */
export const canRemoveHere = (row: AccessRow) => row.via.some((v) => v.kind === 'direct');

/**
 * The roles held DIRECTLY, as `/admin/set-role` expects them back.
 *
 * Without the implied role. An account created through `/admin/create-user`,
 * which is how every agent is made, carries `user` as a direct row, and
 * `/admin/roles` deliberately does not list `user`. Sent back with a new pick,
 * `planRoleSave` refused it as unlisted, so no role could be added to an agent
 * at all (found giving the staging dev agent `dev-agent`, 2026-09-27). Leaving
 * it out loses nothing: the server treats the default role as implied.
 */
export function directRoles(rows: AccessRow[]): string[] {
  return rows
    .filter(canRemoveHere)
    .map((r) => r.name)
    .filter((name) => !isImpliedRole(name));
}

/**
 * Does EVERY account have this, whatever anybody does?
 *
 * Jon, 2026-08-28: the `account` row is not worth a line, because it is assumed
 * — every account on the platform reaches the Account portal, the entitlement is
 * conferred in `ALWAYS_ENTITLED_APPS` rather than by a row, and no control on
 * this screen can change it. A line that is identical on every account and
 * cannot be acted on is a line that trains people to skim the table.
 *
 * ── `some`, NOT `every` — corrected 2026-08-28 ───────────────────────
 *
 * The first version required every source to be the implied one, which hid
 * `account` on a plain account and showed it on anybody who also happened to be
 * in a group granting it. That is the wrong way round: a group grant on top of a
 * universal entitlement changes nothing, so the row is MORE redundant, not less,
 * and removing the group would not take the application away.
 *
 * DERIVED FROM THE PROVENANCE, not from the slug. Writing `row.name ===
 * 'account'` here would be a copy of a list that lives in the auth service, and
 * this file already carries the scar from the last such copy — the Users table
 * tinted `admin` amber until it was renamed and the highlight silently matched
 * nothing. `implied/everybody` is the server saying exactly this, so a second
 * always-entitled application is hidden the day it is added, with nobody
 * editing this.
 *
 * The break-glass entitlement is NOT hidden. It is also implied and also has no
 * row to remove, but it applies to one account in the organisation rather than
 * to all of them, and on the account that has it that is the single most
 * important line in the table.
 */
export const impliedForEverybody = (row: AccessRow) =>
  row.via.some((v) => v.kind === 'implied' && v.reason === 'everybody');

function Applications({
  user,
  access,
  onChanged,
}: {
  user: AdminUser;
  access: Access;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const all = useAsync(() => adminApi.applications());
  const action = useAction();
  const mayGrantApps = useCan('application:write');

  const save = (next: string[], describeIt: (r: string[]) => string) =>
    void action
      .run(async () => describeIt((await adminApi.setUserApplications(user.id, next)).applications))
      .then((ok) => {
        if (ok) onChanged();
      });

  /* Everything except what every account has anyway — see `impliedForEverybody`. */
  const rows = access.applications.filter((row) => !impliedForEverybody(row));

  return (
    <Panel
      title="Applications"
      info={
        <InfoBubble label="What this list shows">
          What this account can open, and where each entitlement came from. Applications every
          account reaches are left out: they are conferred in code rather than by a row, so there is
          nothing here that could change them.
        </InfoBubble>
      }
      action={
        <Button
          disabled={action.running || !mayGrantApps}
          title={mayGrantApps ? undefined : whyDisabled('granting applications')}
          onClick={() => setAdding(true)}
        >
          + Grant application
        </Button>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}
      {access.applications.length === 0 ? (
        <EmptyState
          title="Reaches nothing at all"
          suggestion="Which should not be possible — every account reaches the Account portal. Worth investigating rather than granting over."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Reaches only what everybody reaches"
          suggestion="No application is entitled to this account specifically, directly or through a group."
        />
      ) : (
        <Table columns={['Application', 'Granted via', '']} actionsLast>
          {rows.map((row) => (
            <tr key={row.name} className="rw-rowhover">
              <Td>
                {/* THE NAME, with the slug beneath it in monospace — the chip
                    rule, and what the Roles table below already does. `row.name`
                    is the SLUG on the wire, so the display name has to come from
                    the applications listing; until that arrives, or if it fails,
                    the slug stands alone rather than the row rendering blank. */}
                <AppName slug={row.name} known={all.state} />
              </Td>
              <Td>
                <Via via={row.via} />
              </Td>
              <Td action>
                <RemoveHold
                  running={action.running}
                  allowed={canRemoveHere(row)}
                  blocked="This comes from a group, or from the platform itself. There is no grant on this account to remove."
                  confirm={`Hold to stop granting ${row.name} directly to this account.`}
                  onHold={() =>
                    save(
                      access.directApplications.filter((a) => a !== row.name),
                      () => `${row.name} no longer granted directly to this account.`,
                    )
                  }
                />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {adding ? (
        <AsyncBoundary state={all.state}>
          {(d) => (
            <PickList
              title="Grant an application to this account"
              hint="A DIRECT grant, which stays with the person if their group membership changes. To let a whole set of people in, grant it to a security group instead."
              options={d.applications
                .filter((a) => a.active && !access.directApplications.includes(a.slug))
                .map((a) => ({ value: a.slug, label: `${a.name} (${a.slug})` }))}
              emptyMessage="Every active application is already granted directly to this account."
              onClose={() => setAdding(false)}
              onConfirm={(picked) => {
                setAdding(false);
                save([...access.directApplications, ...picked], (r) =>
                  r.length ? `Granted directly: ${r.join(', ')}.` : 'All direct grants removed.',
                );
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

function Roles({
  user,
  access,
  onChanged,
}: {
  user: AdminUser;
  access: Access;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const known = useAsync(() => adminApi.roles());
  const action = useAction();
  const maySetRoles = useCan('user:set-role');

  /**
   * The server's list, or the roles already held if it has not arrived.
   *
   * The fallback matters: a Remove must not be refused by our own guard because
   * a second request is still in flight. Every name in `direct` came from the
   * server's own provenance read, so it is a list the server already recognises.
   */
  /** The roles held DIRECTLY — what `/admin/set-role` writes, and the only set
   *  this control may send. */
  const direct = directRoles(access.roles);
  const knownRoles = known.state.status === 'ready' ? known.state.data.roles : direct;
  const labels = known.state.status === 'ready' ? known.state.data.labels : undefined;

  const save = (next: string[], known: string[]) =>
    void action
      .run(async () => {
        // Every decision about what to send lives in `planRoleSave`, so the
        // multi-role case stays testable without rendering this tab. It sends an
        // ARRAY rather than a joined string, which is the whole of org-admin#28.
        const plan = planRoleSave(next, known);
        if (!plan.ok) throw new Error(plan.problem);
        await adminApi.setRole(user.id, plan.role);
        return plan.message;
      })
      .then((ok) => {
        if (ok) onChanged();
      });

  /*
    The DEFAULT role is left out (Jon, 2026-08-28): `user` is on every account,
    confers nothing, and `/admin/roles` does not even offer it — so a row for it
    is a line that is identical everywhere and cannot be acted on.

    `isImpliedRole` rather than a literal here, so the one definition of "the
    role that means no role" stays in lib/roles.ts with the reasoning that put it
    there.
  */
  const rows = access.roles.filter((row) => !isImpliedRole(row.name));

  return (
    <Panel
      title="Roles"
      info={
        <InfoBubble label="What a platform role is">
          What this account may do to the platform itself, as distinct from which applications it
          can open. Reviewed in a pull request in the auth service, unlike an app role. The default
          role every account holds is not listed: it confers nothing and cannot be removed.
        </InfoBubble>
      }
      action={
        <Button
          disabled={action.running || !maySetRoles}
          title={maySetRoles ? undefined : whyDisabled('changing roles')}
          onClick={() => setAdding(true)}
        >
          + Assign role
        </Button>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}
      {rows.length === 0 ? (
        <EmptyState
          title="No platform role"
          suggestion="The account can sign in and reach whatever it is entitled to, and can administer nothing."
        />
      ) : (
        <Table columns={['Role', 'Type', 'Granted via', '']} actionsLast>
          {rows.map((row) => (
            <tr key={row.name} className="rw-rowhover">
              <Td>
                <div>{roleLabel(row.name, labels)}</div>
                {/* The slug as well as the name: it is what the column stores
                    and what an API call sends, so somebody reading a log line or
                    writing a script needs to see it here. */}
                <code style={{ fontSize: '.78rem', color: C.muted }}>{row.name}</code>
              </Td>
              {/* Spelled out because it is about to stop being the only kind:
                  app roles are the other half of RidgeWill/auth#146 and are not
                  built yet. A column that always says the same thing today is
                  cheaper than a column added later to a screen people have
                  learned to read. */}
              <Td>Platform</Td>
              <Td>
                <Via via={row.via} />
              </Td>
              <Td action>
                <RemoveHold
                  running={action.running}
                  allowed={canRemoveHere(row)}
                  blocked="This role comes from a group. Take the person out of that group to withdraw it."
                  confirm={`Hold to take ${roleLabel(row.name, labels)} off this account.`}
                  onHold={() => save(direct.filter((r) => r !== row.name), knownRoles)}
                />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {adding ? (
        <AsyncBoundary state={known.state}>
          {(d) => (
            <PickList
              title="Assign a platform role"
              hint="Assigned to this account directly. The account keeps it whatever happens to their group membership."
              options={d.roles
                .filter((r) => !direct.includes(r))
                .map((r) => ({
                  value: r,
                  label: d.labels?.[r]
                    ? `${d.labels[r].label} — ${d.labels[r].description}`
                    : r,
                }))}
              emptyMessage="This account already holds every assignable role directly."
              onClose={() => setAdding(false)}
              onConfirm={(picked) => {
                setAdding(false);
                save([...direct, ...picked], d.roles);
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

/**
 * App roles — names a consuming application matches on, which auth cannot check.
 *
 * A SEPARATE section from platform roles rather than more rows in that table,
 * and the separation is the honest one. A platform role is checked against a
 * statement in the auth service and reviewed in a pull request; an app role is a
 * string auth stores and does not interpret. Putting them in one table would
 * make "what can this person do" look like a single answerable question, when
 * half the answer lives in another codebase.
 *
 * Assignment is by ID, not name: a name is unique only within its application,
 * so `admin` is not an answer to "which role".
 */
function AppRolesHeld({
  user,
  access,
  onChanged,
}: {
  user: AdminUser;
  access: Access;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const apps = useAsync(() => adminApi.applications());
  const action = useAction();
  const mayGrantApps = useCan('application:write');

  const save = (next: string[], describeIt: () => string) =>
    void action
      .run(async () => {
        await adminApi.setUserAppRoles(user.id, next);
        return describeIt();
      })
      .then((ok) => {
        if (ok) onChanged();
      });

  return (
    <Panel
      title="App roles"
      info={
        <InfoBubble label="What an app role is">
          A name a consuming application matches on. Auth stores it and who holds it, and cannot
          interpret it &mdash; so nothing on this platform can tell a typo from a new role, and what
          it permits is decided in that application&rsquo;s own codebase.
        </InfoBubble>
      }
      action={
        <Button
          disabled={action.running || !mayGrantApps}
          title={mayGrantApps ? undefined : whyDisabled('assigning app roles')}
          onClick={() => setAdding(true)}
        >
          + Assign app role
        </Button>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}
      {access.appRoles.length === 0 ? (
        <EmptyState
          title="Holds no app role"
          suggestion="Each application decides what its own roles mean; this platform stores the names and cannot check them."
        />
      ) : (
        <Table columns={['Role', 'Application', 'Granted via', '']} actionsLast>
          {access.appRoles.map((row) => (
            <tr key={`${row.appSlug} ${row.name}`} className="rw-rowhover">
              <Td>
                <div>{row.label}</div>
                {/* The string the application matches on, shown because it is
                    the only thing that actually travels in the token. */}
                <code style={{ fontSize: '.78rem', color: C.muted }}>{row.name}</code>
              </Td>
              <Td>{row.appSlug}</Td>
              <Td>
                <AppRoleVia via={row.via} />
              </Td>
              <Td action>
                <RemoveHold
                  running={action.running}
                  allowed={heldDirectly(row)}
                  blocked={
                    row.via.some((v) => v.kind === 'break-glass')
                      ? 'Global Admin holds every app role in code. There is no assignment to remove — take away the platform role instead.'
                      : 'This comes from a group. Take the person out of that group to withdraw it.'
                  }
                  confirm={`Hold to withdraw ${row.name} for ${row.appSlug}.`}
                  onHold={() =>
                    save(
                      access.directAppRoleIds.filter((id) => id !== row.id),
                      () => `${row.name} withdrawn for ${row.appSlug}.`,
                    )
                  }
                />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {adding ? (
        <AsyncBoundary state={apps.state}>
          {(d) => (
            <AppRolePicker
              applications={d.applications
                .filter((a) => a.active)
                .map((a) => ({ id: a.id, slug: a.slug, name: a.name }))}
              alreadyHeld={access.directAppRoleIds}
              onClose={() => setAdding(false)}
              onConfirm={(picked: string[]) => {
                setAdding(false);
                save([...access.directAppRoleIds, ...picked], () => `Assigned ${picked.length} app role${picked.length === 1 ? '' : 's'}.`);
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

const heldDirectly = (row: HeldAppRole) => row.via.some((v) => v.kind === 'direct');

function AppRoleVia({ via }: { via: HeldAppRole['via'] }) {
  return (
    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
      {via.map((v, i) => (
        <Badge key={i} tone={v.kind === 'direct' ? 'good' : 'neutral'}>
          {v.kind === 'direct' ? 'Direct' : v.kind === 'group' ? v.slug : 'Global Admin'}
        </Badge>
      ))}
    </span>
  );
}

function Groups({
  user,
  access,
  onChanged,
}: {
  user: AdminUser;
  access: Access;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const all = useAsync(() => adminApi.securityGroups());
  const action = useAction();
  const mayEditGroups = useCan('securityGroup:write');
  const inGroups = access.groups.map((g) => g.slug);

  return (
    <Panel
      title="Security groups"
      info={
        <InfoBubble label="What a group carries">
          A group carries platform roles, app roles and application entitlements, and everybody in it
          holds all of them. Taking somebody out withdraws whatever the group gave them &mdash;
          anything they also hold directly stays.
        </InfoBubble>
      }
      action={
        <Button
          disabled={action.running || !mayEditGroups}
          title={mayEditGroups ? undefined : whyDisabled('changing group membership')}
          onClick={() => setAdding(true)}
        >
          + Add to group
        </Button>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}
      {access.groups.length === 0 ? (
        <EmptyState
          title="In no groups"
          suggestion="Every account joins Basic Users on creation, so an account in none has been taken out of it deliberately — or the seed did not run."
        />
      ) : (
        <Table columns={['Group', '']} actionsLast>
          {access.groups.map((g) => (
            <tr key={g.slug} className="rw-rowhover">
              <Td>{g.name}</Td>
              <Td action>
                <RemoveHold
                  running={action.running}
                  allowed
                  confirm={`Hold to take this account out of ${g.name}.`}
                  onHold={() =>
                    void action
                      .run(async () => {
                        const r = await adminApi.removeSecurityGroupMember(g.slug, user.id);
                        return r.removed
                          ? `Taken out of ${g.name}.`
                          : `Was not in ${g.name}.`;
                      })
                      .then((ok) => {
                        if (ok) onChanged();
                      })
                  }
                />
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {adding ? (
        <AsyncBoundary state={all.state}>
          {(d) => (
            <PickList
              title="Add this account to a group"
              hint="They gain whatever the group entitles and whatever role it carries, immediately."
              options={d.groups
                .filter((g) => !inGroups.includes(g.slug))
                .map((g) => ({ value: g.slug, label: `${g.name} (${g.members})` }))}
              emptyMessage="This account is already in every group."
              onClose={() => setAdding(false)}
              onConfirm={(picked) => {
                setAdding(false);
                void action
                  .run(async () => {
                    // One call per group: the endpoint adds people to ONE group,
                    // which is the right shape for its own screen. Sequential so
                    // a failure part-way leaves a report of what did happen
                    // rather than an unordered pile of rejections.
                    const joined: string[] = [];
                    for (const slug of picked) {
                      await adminApi.addSecurityGroupMembers(slug, [user.id]);
                      joined.push(slug);
                    }
                    return `Added to ${joined.join(', ')}.`;
                  })
                  .then((ok) => {
                    if (ok) onChanged();
                  });
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

/**
 * Every Remove on this tab, as a HOLD (Jon, 2026-08-28).
 *
 * ── Why a hold and not a click, on a screen showing one account ──────
 *
 * The argument that made the group members table a hold was the neighbourhood:
 * a row in a long list somebody is scrolling. This tab is one account, so that
 * argument does not apply — and it was the wrong argument to reason from.
 *
 * What actually matters is that these four tables look identical and mean very
 * different things. Removing a group membership can withdraw a role, an app
 * role and several entitlements at once, from a row that says only "Basic
 * Users"; removing a direct grant takes away one thing. Every row is a Remove
 * at the end of a line, and the reader is a person moving down four tables in
 * sequence. A hold is a different KIND of input from a click — it cannot be
 * produced by a reflex, and what you are looking at while you do it is the row.
 *
 * ── The disabled case is still a plain, greyed control ───────────────
 *
 * A hold that can never complete would be a control that appears to invite an
 * action and then does nothing, which is worse than one that says it is off.
 * `HoldButton` takes `disabled`, and `blocked` is the sentence explaining why —
 * a greyed control with no explanation reads as a broken feature.
 */
function RemoveHold({
  running,
  allowed,
  blocked,
  confirm,
  onHold,
}: {
  running: boolean;
  allowed: boolean;
  /** Why it cannot be pressed. Required whenever `allowed` can be false. */
  blocked?: string;
  /** What holding will do, on the control that does it. */
  confirm: string;
  onHold: () => void;
}) {
  return (
    <HoldButton
      disabled={running || !allowed}
      title={allowed ? confirm : blocked}
      onHold={onHold}
    >
      Remove
    </HoldButton>
  );
}


